import { ADANA_DISTRICTS, ADANA_OFFICIAL_SOURCES } from "./adana";

export interface NeighborhoodSEO {
  ilce: string;
  ilceSlug: string;
  mahalle?: string;
  mahalleSlug?: string;
  title: string;
  metaDescription: string;
  h1: string;
  intro: string;
  guideSections: { heading: string; content: string }[];
  popularPropertyTypes: string[];
  checklist: string[];
  sources: { title: string; url: string }[];
  faqs: { question: string; answer: string }[];
}

const districtPages: Record<string, NeighborhoodSEO> = Object.fromEntries(
  ADANA_DISTRICTS.map((district) => [district.slug, {
    ilce: district.name,
    ilceSlug: district.slug,
    title: `${district.name} Satılık ve Kiralık Emlak | Adana Bölge Rehberi`,
    metaDescription: `Adana ${district.name} satılık ve kiralık gayrimenkul ilanları. ${district.focus} için mülk kontrol listesi, bölge rehberi ve Corner Gayrimenkul danışmanlığı.`,
    h1: `${district.name} gayrimenkul rehberi`,
    intro: district.description,
    guideSections: [
      {
        heading: `${district.name} için aramanızı netleştirin`,
        content: `${district.description} İlgilendiğiniz mülkün özelliklerini, bütçenizi ve kullanmayı düşündüğünüz dönemi belirleyerek ilçe içindeki seçenekleri karşılaştırın.`,
      },
      {
        heading: "İlan fiyatını mülkün özellikleriyle birlikte okuyun",
        content: "Satılık ve kiralık fiyatları; konum, net alan, bina özellikleri ve kullanım durumuna göre değişir. Aynı ilçede yer alan mülklerin birbirinin eşdeğeri olduğunu varsaymayın. Buradaki ilan fiyatları bölge ortalaması veya gerçekleşmiş satış verisi değildir.",
      },
    ],
    popularPropertyTypes: ["Konut", "İşyeri", "Arsa / arazi"],
    checklist: district.checks,
    sources: ADANA_OFFICIAL_SOURCES,
    faqs: [
      {
        question: `${district.name} satılık ve kiralık ilanlarına nasıl ulaşabilirim?`,
        answer: `Bu sayfadaki ilanlar yalnızca ${district.name} ilçesiyle eşleşen yayımlanmış portföylerden oluşur. Tüm satılık veya kiralık ilanlar sayfasında ilçe filtresini kullanarak aramanızı daraltabilirsiniz. İlan bulunmadığında ihtiyacınızı danışmanımıza iletebilirsiniz.`,
      },
      {
        question: `${district.name} için mülk ziyaretinde hangi konulara odaklanmalıyım?`,
        answer: `${district.checks.join(" ")} İlan bilgilerini yerinde gördüğünüz durum ve mülkün belgeleriyle karşılaştırın.`,
      },
      {
        question: `${district.name} genelindeki ortalama fiyat bu sayfada var mı?`,
        answer: "Bu rehber ilçe geneli için fiyat ortalaması veya getiri tahmini yayımlamaz. Bir mülkü değerlendirmek için güncel ilan özelliklerini, benzer mülkleri ve belgelere dayalı bilgileri birlikte inceleyin.",
      },
    ],
  }]),
);

const neighborhoods = [
  {
    ilce: "Seyhan", ilceSlug: "seyhan", mahalle: "Gürselpaşa", mahalleSlug: "gurselpasa",
    intro: "Gürselpaşa’da daire ararken site hizmetleri, aidat, net alan ve günlük ulaşım rotanızı birlikte değerlendirin. Mahalle bilgisi kadar mülkün bulunduğu yapı ve sokak da seçiminizi etkiler.",
    checklist: ["Site aidatına hangi hizmetlerin dahil olduğunu öğrenin.", "Net alanı ve oda planını mülk ziyaretiyle karşılaştırın.", "Otopark, asansör ve ortak alanların mevcut durumunu inceleyin."],
  },
  {
    ilce: "Seyhan", ilceSlug: "seyhan", mahalle: "Pınar", mahalleSlug: "pinar",
    intro: "Pınar Mahallesi’nde satılık ve kiralık daireleri bütçe, net kullanım alanı ve günlük ihtiyaçlarınıza göre karşılaştırın. Ulaşım ve bina özelliklerini ilanın tam konumu üzerinden inceleyin.",
    checklist: ["Günlük ulaşım rotanızı ilanın adresinden planlayın.", "Cephe ve güneş alma durumunu farklı saatlerde inceleyin.", "Bina aidatı ve bakım ihtiyaçlarını öğrenin."],
  },
  {
    ilce: "Çukurova", ilceSlug: "cukurova", mahalle: "Güzelyalı", mahalleSlug: "guzelyali",
    intro: "Güzelyalı’da konut seçiminde cephe, açık alan ve manzara beklentinizi ilanın gerçek özellikleriyle karşılaştırın. Daire ve müstakil konutların bakım ve kullanım maliyetlerini ayrı değerlendirin.",
    checklist: ["Manzara bilgisini mülkün kendi konumundan doğrulayın.", "Bahçe veya açık alanın kullanım sınırlarını öğrenin.", "Aidat ve düzenli bakım maliyetlerini karşılaştırın."],
  },
  {
    ilce: "Çukurova", ilceSlug: "cukurova", mahalle: "Mahfesığmaz", mahalleSlug: "mahfesigmaz",
    intro: "Mahfesığmaz’da konut veya işyeri ararken caddeye erişim, gürültü, otopark ve kullanım amacınızı birlikte değerlendirin. Aynı mahalledeki mülkleri bina özellikleriyle karşılaştırın.",
    checklist: ["Cadde yoğunluğunu farklı saatlerde gözlemleyin.", "Otopark ve mülke giriş koşullarını kontrol edin.", "İşyeri düşünüyorsanız kullanım koşullarını ilgili belgelerden doğrulayın."],
  },
];

const neighborhoodPages: Record<string, NeighborhoodSEO> = Object.fromEntries(
  neighborhoods.map((neighborhood) => [`${neighborhood.ilceSlug}-${neighborhood.mahalleSlug}`, {
    ...districtPages[neighborhood.ilceSlug],
    ...neighborhood,
    title: `${neighborhood.mahalle} Satılık ve Kiralık Emlak | Adana ${neighborhood.ilce}`,
    metaDescription: `Adana ${neighborhood.ilce} ${neighborhood.mahalle} Mahallesi satılık ve kiralık gayrimenkul ilanları. Mülk kontrol listesi ve Corner Gayrimenkul danışmanlığı.`,
    h1: `${neighborhood.mahalle} Mahallesi gayrimenkul rehberi`,
    guideSections: [
      { heading: `${neighborhood.mahalle} için mülk seçimi`, content: neighborhood.intro },
      { heading: "İlan bilgilerini doğrulayın", content: "İlanda belirtilen net alan, oda sayısı, cephe, kat ve site özelliklerini yerinde inceleyin. Talep edilen fiyatı benzer özellikteki güncel mülklerle birlikte değerlendirin; mahalle geneline tek bir fiyat veya getiri atfetmeyin." },
    ],
    faqs: [
      {
        question: `${neighborhood.mahalle} ilanları hangi ilçede yer alıyor?`,
        answer: `Bu sayfa, Adana ${neighborhood.ilce} ilçesinde ${neighborhood.mahalle} Mahallesi olarak kaydedilmiş yayımlanmış ilanları gösterir. İlana ait konum ve adres bilgisini ilan detayından kontrol edebilirsiniz.`,
      },
      { question: `${neighborhood.mahalle} için daire ziyaretinde nelere bakmalıyım?`, answer: neighborhood.checklist.join(" ") },
    ],
  }]),
);

export const DISTRICTS_AND_NEIGHBORHOODS: Record<string, NeighborhoodSEO> = {
  ...districtPages,
  ...neighborhoodPages,
};
